import { useState } from "react";
import { Check, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANGUAGES } from "@/i18n";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Globe-dropdown language switcher for the public marketing/legal surface.
 * Reads and sets the active i18next language; the choice is persisted to
 * localStorage by the browser-language detector (see {@link src/i18n.ts}), so
 * it survives reloads and applies on the next visit.
 *
 * <p>Two placements share this one component:</p>
 * <ul>
 *   <li>inline in the landing navbar and the legal-page header (default);</li>
 *   <li>{@code floating} — fixed to the top-right corner of a bare page such as
 *       the sign-in interstitial, which has no chrome of its own.</li>
 * </ul>
 *
 * <p><b>{@code contentClassName} exists for one reason: the dropdown is PORTALLED to
 * {@code document.body}, so it leaves the stacking context of whatever opened it.</b>
 * {@code PopoverContent} ships at {@code z-50}, and z-index beats DOM order between
 * two siblings of the root, so inside the {@code z-80} auth modal the list would open
 * BEHIND the card it belongs to — the trigger looks dead rather than broken. Any host
 * that sits above z-50 has to say so.</p>
 */
export function LanguageSwitcher({
  floating = false,
  className,
  contentClassName,
}: {
  floating?: boolean;
  className?: string;
  /** Extra classes for the portalled dropdown. Needed above z-50; see the note above. */
  contentClassName?: string;
}) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);

  const active = i18n.resolvedLanguage ?? "en";
  const activeLabel =
    SUPPORTED_LANGUAGES.find((l) => l.code === active)?.label ?? "English";

  const choose = (code: string) => {
    void i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className={cn(floating && "fixed right-4 top-4 z-50", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("lang.switch")}
            title={t("lang.current", { label: activeLabel })}
            className="flex items-center gap-1 rounded-full border border-slate-200 bg-white/80 px-2.5 py-1.5 text-sm font-medium text-slate-600 shadow-sm backdrop-blur transition hover:bg-slate-100 hover:text-slate-900"
          >
            <Globe className="h-4 w-4" aria-hidden="true" />
            <span className="uppercase">{active}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className={cn("w-40 p-1", contentClassName)}>
          <ul role="listbox" aria-label={t("lang.switch")}>
            {SUPPORTED_LANGUAGES.map((l) => {
              const selected = l.code === active;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => choose(l.code)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition hover:bg-slate-100",
                      selected
                        ? "font-semibold text-slate-900"
                        : "text-slate-600",
                    )}
                  >
                    {l.label}
                    {selected && (
                      <Check className="h-4 w-4 text-emerald-500" aria-hidden="true" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
